import type { VariantAtom } from '@core/Entities/Search/Atoms/AvailableVariantsAtom.entity';
import { RANGE_FILTERS } from '@core/Services/Search/Filters.constants';
import { BODY_TYPES } from '@core/Utils/VehicleMappedMessages/BodyTypeMappings.constants';
import { COLORS } from '@core/Utils/VehicleMappedMessages/ColorMappings.constants';
import { FUEL_TYPES } from '@core/Utils/VehicleMappedMessages/FuelTypeMappings.constants';
import { getOptionsWithAggs } from '@plp/Utils/Filters/GetOptionsWithAggs';

import type { SectionConfig, SectionOption } from './VehicleInterlinking.entities';

export const getTopOptions = (options: SectionOption[], limit = 6): SectionOption[] => {
  return options
    .filter(option => option.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

export const getAllOptions = (options: SectionOption[]): SectionOption[] => {
  return options.filter(option => option.count > 0).sort((a, b) => b.count - a.count);
};

const modelsConfig: SectionConfig = {
  key: 'models',
  headingKey: 'modelsForSale',
  messageKey: null,
  getOptions: (aggs, make) => {
    if (!aggs.model?.[make]?.models) return [];
    return Object.entries(aggs.model[make].models)
      .map(([id, model]) => ({ id, count: model.count, label: model.label }))
      .filter(({ count }) => count > 0);
  },
  createFilters: (makeName, _, modelId) => [
    { id: 'make', value: makeName },
    { id: 'model', value: modelId },
  ],
  formatCtaText: (makeDisplay, _, id) => [makeDisplay, id].filter(Boolean).join(' '),
  getDisplayName: option => option.label || option.id,
};

const pricesConfig: SectionConfig = {
  key: 'prices',
  headingKey: 'prices',
  messageKey: null,
  getOptions: aggs => {
    const priceBuckets = aggs.price ?? {};
    let eligibleVehicleCount = 0;

    return RANGE_FILTERS.price.flatMap(price => {
      eligibleVehicleCount += priceBuckets[price] ?? 0;
      return eligibleVehicleCount > 0
        ? [
            {
              id: price,
              count: eligibleVehicleCount,
              label: `Under £${Number(price).toLocaleString('en-GB')}`,
            },
          ]
        : [];
    });
  },
createFilters: (makeName, modelWithMakeName, priceLte) => [
    ...(makeName ? [{ id: 'make' as const, value: makeName }] : []),
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'price__lte', value: priceLte },
  ],
  formatCtaText: (makeDisplay, modelDisplay, priceDisplay) =>
    [makeDisplay, modelDisplay, priceDisplay].filter(Boolean).join(' '),
  getDisplayName: option => option.label || option.id,
};

const makesConfig: SectionConfig = {
  key: 'makes',
  headingKey: 'makesForSale',
  messageKey: null,
  getOptions: aggs =>
    Object.entries(aggs.make ?? {}).map(([id, make]) => ({
      id,
      count: make.count,
      label: make.label,
    })),
  createFilters: (_, __, makeId) => [{ id: 'make', value: makeId }],
  formatCtaText: (_, __, makeDisplayName) => makeDisplayName,
  getDisplayName: option => option.label || option.id,
};

const trimsConfig: SectionConfig = {
  key: 'trims',
  headingKey: 'modelVariants',
  messageKey: null,
  getOptions: (aggs, makeName, modelWithMakeName) => {
    // First try to use the flattened variants array
    if (aggs.variants && aggs.variants.length > 0) {
      const filteredVariants = aggs.variants.filter(
        (v: VariantAtom) => v.make === makeName && v.model === modelWithMakeName,
      );

      if (filteredVariants.length > 0) {
        return filteredVariants.map((variant: VariantAtom) => ({
          id: variant.key,
          count: variant.count,
          label: variant.displayName,
        }));
      }
    }

    // Fall back to using the nested variant structure if available
    if (modelWithMakeName && aggs.variant?.[makeName]?.models[modelWithMakeName]?.variants) {
      const variants = aggs.variant[makeName].models[modelWithMakeName].variants;
      return Object.entries(variants).reduce((acc, [variantId, variant]) => {
        if (variant.count > 0) {
          acc.push({ id: variantId, count: variant.count, label: variant.label });
        }
        return acc;
      }, [] as SectionOption[]);
    }

    return [];
  },
  createFilters: (makeName, modelWithMakeName, variantId) => [
    ...(makeName ? [{ id: 'make' as const, value: makeName }] : []),
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'variant-type', value: variantId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, trimDisplay) =>
    [makeDisplay, modelDisplay, trimDisplay].filter(Boolean).join(' '),
  getDisplayName: option => option.label || option.id,
};

export const bodyTypesConfig: SectionConfig = {
  key: 'body-types',
  headingKey: 'bodyTypes',
  messageKey: 'body-types',
  getOptions: aggs => {
    if (!aggs['body-type']) return [];
    return getOptionsWithAggs(BODY_TYPES, aggs['body-type']);
  },
  createFilters: (makeName, modelWithMakeName, bodyTypeId) => [
    ...(makeName ? [{ id: 'make' as const, value: makeName }] : []),
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'body-type', value: bodyTypeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, bodyTypeDisplay) =>
    [makeDisplay, modelDisplay, bodyTypeDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.labelKey as keyof typeof messagesObj] || option.id,
};

const fuelTypesConfig: SectionConfig = {
  key: 'fuel-types',
  headingKey: 'fuelType',
  messageKey: 'fuel-types',
  getOptions: aggs => {
    if (!aggs['fuel-type']) return [];
    return getOptionsWithAggs(FUEL_TYPES, aggs['fuel-type']);
  },
  createFilters: (makeName, modelWithMakeName, fuelTypeId) => [
    ...(makeName ? [{ id: 'make' as const, value: makeName }] : []),
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'fuel-type', value: fuelTypeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, fuelTypeDisplay) =>
    [makeDisplay, modelDisplay, fuelTypeDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.labelKey as keyof typeof messagesObj] || option.id,
};

const filterOutTransmissions = new Set<string>(['unknown', 'none']);

const transmissionsConfig: SectionConfig = {
  key: 'transmissions',
  headingKey: 'transmissions',
  messageKey: 'transmissions',
  getOptions: aggs => {
    if (!aggs['gear-box']) return [];

    return Object.entries(aggs['gear-box'])
      .map(([id, count]) => ({ id, count: count as number, label: id }))
      .filter(({ count, id }) => count > 0 && !filterOutTransmissions.has(id));
  },
  createFilters: (makeName, modelWithMakeName, gearboxId) => [
    ...(makeName ? [{ id: 'make' as const, value: makeName }] : []),
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'gear-box', value: gearboxId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, gearboxDisplay) =>
    [makeDisplay, modelDisplay, gearboxDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.id as keyof typeof messagesObj] || option.id,
};

const engineSizesConfig: SectionConfig = {
  key: 'engine-sizes',
  headingKey: 'engineSizes',
  messageKey: null,
  getOptions: aggs => {
    if (!aggs['engine-size']) return [];
    return Object.entries(aggs['engine-size'])
      .map(([id, count]) => ({ id, count: count as number, label: id }))
      .filter(({ count }) => count > 0);
  },
  createFilters: (makeName, modelWithMakeName, engineSizeId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'engine-size__gte', value: engineSizeId },
    { id: 'engine-size__lte', value: engineSizeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, engineSizeDisplay) =>
    [makeDisplay, modelDisplay, engineSizeDisplay].filter(Boolean).join(' '),
  getDisplayName: option => option.id,
};

// "other" color has different ids in different locales
const otherColorIds = new Set(['othercolor', 'other']);

const colorsConfig: SectionConfig = {
  key: 'colors',
  headingKey: 'colors',
  messageKey: 'colors',
  getOptions: aggs => {
    if (!aggs.color) return [];
    const options = getOptionsWithAggs(COLORS, aggs.color).filter(
      option => !otherColorIds.has(option.id) && !otherColorIds.has(option.labelKey),
    );
    return options;
  },
  createFilters: (makeName, modelWithMakeName, colorId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'color', value: colorId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, colorDisplay) =>
    [makeDisplay, modelDisplay, colorDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) => messagesObj[`${option.labelKey}.one`] || option.id,
};

export const MAKE_MODEL_INTERLINKING_SECTIONS: SectionConfig[] = [
  trimsConfig,
  pricesConfig,
  bodyTypesConfig,
  fuelTypesConfig,
  transmissionsConfig,
  engineSizesConfig,
  colorsConfig,
];

export const MAKE_INTERLINKING_SECTIONS: SectionConfig[] = [
  modelsConfig,
  pricesConfig,
  bodyTypesConfig,
  transmissionsConfig,
  fuelTypesConfig,
];

export const PLP_VEHICLE_INTERLINKING_SECTIONS: SectionConfig[] = [
  makesConfig,
  pricesConfig,
  bodyTypesConfig,
  transmissionsConfig,
  fuelTypesConfig,
];

export const getInterlinkingSections = ({ model }: { model: string | undefined }) => {
  return model ? MAKE_MODEL_INTERLINKING_SECTIONS : MAKE_INTERLINKING_SECTIONS;
};
